import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { seesTeamHr, type PolicySubject } from "@alm/policy";
import { chosenFacility } from "@/lib/facility/facility-choice";
import { useCanAnywhere, usePolicy } from "@/lib/policy";
import { canHere } from "@/lib/policy/policy-here";
import { guardOwnFeature, guardRoute } from "@/lib/policy/route-guard";
import { HrSectionNav } from "./-components/hr-section-nav";

/** My documents is the reader's own file and the API asks no permission for it; guarding the
 * subtree on `hr:read` shut caregivers out of their own paperwork. */
const MINE = "/hr/my-documents";

export const Route = createFileRoute("/_authenticated/_workforce/hr")({
  beforeLoad: ({ context, location }) => {
    if (location.pathname === MINE) return guardOwnFeature(context.me, "hr");
    guardRoute(context.me, "hr");
    // A floor role's hr:read is its own file: the team's screens open to those who see the team's.
    const subject = context.me?.subject as PolicySubject | undefined;
    const here = subject ? chosenFacility(subject) : null;
    if (subject && !seesTeamHr((permission) => canHere(subject, permission, here))) {
      throw redirect({ to: MINE, replace: true });
    }
  },
  component: HrLayout,
});

/** The strip along the top moves between HR screens. My documents is the reader's own, under You,
 * so it opens without HR's strip. */
function HrLayout() {
  const { can, feature } = usePolicy();
  // The register opens to a building's administrator wherever she is one, as the API does.
  const readsStaff = useCanAnywhere("staff:read");
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Someone with only their own file has one screen, so no strip.
  if (!seesTeamHr(can) || pathname === MINE) return <Outlet />;

  return (
    <>
      <HrSectionNav register={readsStaff && feature("user-management")} bulk={can("hr:manage")} />
      <Outlet />
    </>
  );
}
